import { AlertType, Coupon, CreateCouponRequest, Season } from '@types';
import { AlertModal, Card, PageHeader } from '_atoms';
import { GetCouponDetails } from '_molecules';
import { AdminService } from '_services';
import { ExcelExport } from '_utils';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdAdd,
  MdClose,
  MdDelete,
  MdDownload,
  MdLocalOffer,
} from 'react-icons/md';

/** Extract YYYY-MM-DD from a date string without UTC day-shift. */
const toCalendarDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

/** Build datetime-local value from a calendar date and HH:mm. */
const toDateTimeLocal = (
  calendarDate: string | null,
  time: string,
): string | null => {
  if (!calendarDate) return null;
  return `${calendarDate}T${time}`;
};

const getFallbackValidFrom = (): string =>
  new Date().toISOString().slice(0, 16);

const getFallbackValidTo = (): string => {
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  return oneYearFromNow.toISOString().slice(0, 16);
};

const emptyCouponForm = (
  validFrom: string | null = null,
  validTo: string | null = null,
): Partial<CreateCouponRequest> => ({
  couponCode: '',
  discountType: 'Percentage',
  discountValue: 100,
  name: '',
  description: '',
  photoUrl: '',
  maxUses: 1,
  maxUsesPerUser: 1,
  validFrom,
  validTo,
  numberOfCoupons: 1,
  timeRangeFrom: null,
  timeRangeTo: null,
});

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [filteredCoupons, setFilteredCoupons] = useState<Coupon[]>([]);
  const [filter, setFilter] = useState<
    'all' | 'isFullyRedeemed' | 'isActive' | 'isExpired'
  >('all');
  const [discountTypeFilter, setDiscountTypeFilter] = useState<
    'all' | 'Percentage' | 'Fixed'
  >('all');
  const [foundCoupon, setFoundCoupon] = useState<Coupon | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [seasonValidFrom, setSeasonValidFrom] = useState<string | null>(null);
  const [seasonValidTo, setSeasonValidTo] = useState<string | null>(null);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });
  const [newCoupon, setNewCoupon] = useState<Partial<CreateCouponRequest>>(() =>
    emptyCouponForm(),
  );

  // Day selection state - all days selected by default
  const [selectedDays, setSelectedDays] = useState<Set<string>>(
    new Set(['M', 'T', 'W', 'TH', 'F', 'S', 'SU']),
  );

  // Day configuration
  const daysOfWeek = [
    { code: 'M', label: 'Mon' },
    { code: 'T', label: 'Tue' },
    { code: 'W', label: 'Wed' },
    { code: 'TH', label: 'Thu' },
    { code: 'F', label: 'Fri' },
    { code: 'S', label: 'Sat' },
    { code: 'SU', label: 'Sun' },
  ];

  // Format selected days for backend (M,W,TH format)
  const formatApplicableDays = (days: Set<string>): string | null => {
    if (days.size === 7) {
      // All days selected, return null
      return null;
    }
    // Return comma-separated string of selected days in week order
    const dayOrder = ['M', 'T', 'W', 'TH', 'F', 'S', 'SU'];
    return dayOrder.filter(day => days.has(day)).join(',');
  };

  // Toggle day selection
  const toggleDay = (dayCode: string) => {
    setSelectedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dayCode)) {
        newSet.delete(dayCode);
      } else {
        newSet.add(dayCode);
      }
      return newSet;
    });
  };

  // Reset days to all selected
  const resetDays = () => {
    setSelectedDays(new Set(['M', 'T', 'W', 'TH', 'F', 'S', 'SU']));
  };

  // Format time from number (e.g., 1025 -> "10:25")
  const formatTimeDisplay = (timeNum: number | null | undefined): string => {
    if (timeNum === null || timeNum === undefined) return 'All hours';
    const hours = Math.floor(timeNum / 100);
    const minutes = timeNum % 100;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Format applicable days for display (e.g., "M,W,TH" -> "Mon, Wed, Thu")
  const formatApplicableDaysDisplay = (
    days: string | null | undefined,
  ): string => {
    if (!days || days === null) return 'All days';
    const dayMap: Record<string, string> = {
      M: 'M',
      T: 'T',
      W: 'W',
      TH: 'Th',
      F: 'F',
      S: 'Sa',
      SU: 'Su',
    };
    return days
      .split(',')
      .map(d => dayMap[d.trim()] || d.trim())
      .join(', ');
  };

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const applyFilter = useCallback(() => {
    let filtered = [...coupons];

    // Apply status filter
    switch (filter) {
      case 'isFullyRedeemed':
        filtered = coupons.filter(coupon => coupon.isFullyRedeemed);
        break;
      case 'isActive':
        filtered = coupons.filter(
          coupon =>
            coupon.isActive && !coupon.isFullyRedeemed && !coupon.isExpired,
        );
        break;
      case 'isExpired':
        filtered = coupons.filter(coupon => coupon.isExpired);
        break;
      case 'all':
      default:
        filtered = coupons;
        break;
    }

    // Apply discount type filter
    if (discountTypeFilter !== 'all') {
      filtered = filtered.filter(
        coupon => coupon.discountType === discountTypeFilter,
      );
    }

    setFilteredCoupons(filtered);
  }, [coupons, filter, discountTypeFilter]);

  const fetchCoupons = useCallback(async () => {
    setIsLoading(true);
    try {
      const response =
        (await AdminService.getAllCoupons()) as unknown as Coupon[];
      setCoupons(response);
    } catch (error) {
      showAlert('error', 'Error', 'Failed to fetch coupons');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  const loadSeasonValidityDefaults = useCallback(async () => {
    try {
      const season =
        (await AdminService.getCurrentSeasonDetails()) as unknown as Season;

      const from = toDateTimeLocal(toCalendarDate(season?.startDate), '00:00');
      const to = toDateTimeLocal(toCalendarDate(season?.endDate), '23:59');

      setSeasonValidFrom(from);
      setSeasonValidTo(to);

      if (from || to) {
        setNewCoupon(prev => ({
          ...prev,
          validFrom: prev.validFrom || from,
          validTo: prev.validTo || to,
        }));
      }
    } catch (error) {
      console.error(
        'Failed to load current season for coupon defaults:',
        error,
      );
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
    loadSeasonValidityDefaults();
  }, [fetchCoupons, loadSeasonValidityDefaults]);

  useEffect(() => {
    applyFilter();
  }, [applyFilter]);

  const handleCreateCoupon = async () => {
    // Validate time range
    if (
      newCoupon.timeRangeFrom !== null &&
      newCoupon.timeRangeFrom !== undefined &&
      newCoupon.timeRangeTo !== null &&
      newCoupon.timeRangeTo !== undefined &&
      newCoupon.timeRangeTo.toString().length === 4 &&
      newCoupon.timeRangeFrom.toString().length === 4 &&
      newCoupon.timeRangeFrom > newCoupon.timeRangeTo
    ) {
      showAlert(
        'error',
        'Invalid Time Range',
        'Start time must be before or equal to end time',
      );
      return;
    }

    setIsCreating(true);
    try {
      const applicableDays = formatApplicableDays(selectedDays);

      // Prefer admin input, then active season dates, then now / one-year fallback
      const validFrom =
        newCoupon.validFrom && newCoupon.validFrom.trim() !== ''
          ? newCoupon.validFrom
          : seasonValidFrom || getFallbackValidFrom();
      const validTo =
        newCoupon.validTo && newCoupon.validTo.trim() !== ''
          ? newCoupon.validTo
          : seasonValidTo || getFallbackValidTo();

      // Build coupon data, ensuring all required fields are present and handling nulls correctly
      const couponData: CreateCouponRequest = {
        couponCode: newCoupon.couponCode || '',
        discountType: newCoupon.discountType || 'Percentage',
        discountValue: newCoupon.discountValue || 100,
        name: newCoupon.name || '',
        description: newCoupon.description || '',
        photoUrl: newCoupon.photoUrl || '',
        maxUses: newCoupon.maxUses || 1,
        maxUsesPerUser: newCoupon.maxUsesPerUser || 1,
        validFrom,
        validTo,
        applicableDays,
        numberOfCoupons: newCoupon.numberOfCoupons || 1,
        timeRangeFrom:
          newCoupon.timeRangeFrom !== null &&
          newCoupon.timeRangeFrom !== undefined
            ? newCoupon.timeRangeFrom
            : null,
        timeRangeTo:
          newCoupon.timeRangeTo !== null && newCoupon.timeRangeTo !== undefined
            ? newCoupon.timeRangeTo
            : null,
      };

      await AdminService.createCoupon(couponData);
      showAlert('success', 'Success', 'Coupon created successfully');
      setIsCreateModalVisible(false);

      setNewCoupon(emptyCouponForm(seasonValidFrom, seasonValidTo));
      resetDays();
      fetchCoupons();
    } catch (error) {
      showAlert('error', 'Error', 'Failed to create coupon');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCoupon = async (couponId: number) => {
    try {
      await AdminService.deleteCoupon(couponId);
      showAlert('success', 'Success', 'Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      showAlert('error', 'Error', 'Failed to delete coupon');
    }
  };

  const handleDownloadActiveCoupons = () => {
    const activeCoupons = coupons.filter(
      coupon => coupon.isActive && !coupon.isFullyRedeemed && !coupon.isExpired,
    );

    if (activeCoupons.length === 0) {
      showAlert(
        'warning',
        'No Active Coupons',
        'There are no active coupons to download.',
      );
      return;
    }

    try {
      ExcelExport.exportCouponsToExcel(activeCoupons);
      showAlert(
        'success',
        'Download Started',
        `Downloading ${activeCoupons.length} active coupons to Excel file.`,
      );
    } catch (error) {
      showAlert(
        'error',
        'Download Failed',
        'Failed to download active coupons. Please try again.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={MdLocalOffer}
        title="Coupon Management"
        description="Manage discount coupons and promotional codes"
        reload={fetchCoupons}
      />

      <GetCouponDetails
        onCouponFound={coupon => {
          console.log('Coupon found:', coupon);
          setFoundCoupon(coupon);
        }}
      />

      {foundCoupon && (
        <Card>
          <div className="mb-2">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Coupon Details
              </h3>
              <button
                onClick={() => setFoundCoupon(null)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                title="Dismiss coupon details"
              >
                <MdClose className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Coupon Code
                </label>
                <p className="text-base font-mono font-semibold text-blue-600">
                  {foundCoupon.couponCode}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Name
                </label>
                <p className="text-sm text-gray-900">{foundCoupon.name}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Discount Type
                </label>
                <p className="text-sm text-gray-900">
                  {foundCoupon.discountType}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Discount Value
                </label>
                <p className="text-base font-semibold text-green-600">
                  {foundCoupon.discountType === 'Percentage'
                    ? `${foundCoupon.discountValue}%`
                    : `$${foundCoupon.discountValue}`}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Uses
                </label>
                <p className="text-sm text-gray-900">{foundCoupon.maxUses}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Uses Per User
                </label>
                <p className="text-sm text-gray-900">
                  {foundCoupon.maxUsesPerUser}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Valid From
                </label>
                <p className="text-sm text-gray-900">
                  {new Date(foundCoupon.validFrom).toLocaleDateString()}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Valid To
                </label>
                <p className="text-sm text-gray-900">
                  {new Date(foundCoupon.validTo).toLocaleDateString()}
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="flex flex-col gap-1">
                  {!foundCoupon.isFullyRedeemed && !foundCoupon.isExpired && (
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center justify-center ${
                        foundCoupon.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {foundCoupon.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {foundCoupon.isFullyRedeemed && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 text-center justify-center">
                      Fully Redeemed
                    </span>
                  )}
                  {foundCoupon.isExpired && !foundCoupon.isFullyRedeemed && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 text-center justify-center">
                      Expired
                    </span>
                  )}
                </div>
              </div>
            </div>

            {foundCoupon.description && (
              <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <p className="text-sm text-gray-900">
                  {foundCoupon.description}
                </p>
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Usage Statistics
                </label>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    Total Redemptions:{' '}
                    <span className="font-semibold">
                      {foundCoupon.totalRedemptions}
                    </span>
                  </p>
                  <p className="text-xs text-gray-600">
                    Unique Users:{' '}
                    <span className="font-semibold">
                      {foundCoupon.uniqueUsers}
                    </span>
                  </p>
                  <p className="text-xs text-gray-600">
                    Remaining Uses:{' '}
                    <span className="font-semibold text-green-600">
                      {foundCoupon.maxUses - foundCoupon.totalRedemptions}
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Created
                </label>
                <p className="text-sm text-gray-900">
                  {new Date(foundCoupon.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Coupons</h2>
          <div className="flex gap-3">
            <button
              onClick={handleDownloadActiveCoupons}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <MdDownload className="w-5 h-5" />
              Download Active Coupons
            </button>
            <button
              onClick={() => {
                resetDays();
                setIsCreateModalVisible(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MdAdd className="w-5 h-5" />
              Create Coupon
            </button>
          </div>
        </div>

        {/* Filter Options */}
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Filter by Status
            </h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  value="all"
                  checked={filter === 'all'}
                  onChange={e => setFilter(e.target.value as any)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">All Coupons</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  value="isActive"
                  checked={filter === 'isActive'}
                  onChange={e => setFilter(e.target.value as any)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  value="isFullyRedeemed"
                  checked={filter === 'isFullyRedeemed'}
                  onChange={e => setFilter(e.target.value as any)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Fully Redeemed</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="filter"
                  value="isExpired"
                  checked={filter === 'isExpired'}
                  onChange={e => setFilter(e.target.value as any)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Expired</span>
              </label>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Filter by Discount Type
            </h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="discountTypeFilter"
                  value="all"
                  checked={discountTypeFilter === 'all'}
                  onChange={e =>
                    setDiscountTypeFilter(
                      e.target.value as 'all' | 'Percentage' | 'Fixed',
                    )
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">All Types</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="discountTypeFilter"
                  value="Percentage"
                  checked={discountTypeFilter === 'Percentage'}
                  onChange={e =>
                    setDiscountTypeFilter(
                      e.target.value as 'all' | 'Percentage' | 'Fixed',
                    )
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Percentage</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="discountTypeFilter"
                  value="Fixed"
                  checked={discountTypeFilter === 'Fixed'}
                  onChange={e =>
                    setDiscountTypeFilter(
                      e.target.value as 'all' | 'Percentage' | 'Fixed',
                    )
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Fixed</span>
              </label>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redemptions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicable Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCoupons.map(coupon => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {coupon.couponCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.discountType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.discountValue}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-600">
                          {coupon.totalRedemptions} / {coupon.maxUses}
                        </span>
                        <span className="text-xs text-gray-500">
                          {coupon.uniqueUsers} unique users
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.validFrom
                        ? new Date(coupon.validFrom).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.validTo
                        ? new Date(coupon.validTo).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className="text-xs">
                        {formatApplicableDaysDisplay(coupon.applicableDays)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon.timeRangeFrom !== null &&
                      coupon.timeRangeFrom !== undefined &&
                      coupon.timeRangeTo !== null &&
                      coupon.timeRangeTo !== undefined ? (
                        <span className="text-xs">
                          {formatTimeDisplay(coupon.timeRangeFrom)} -{' '}
                          {formatTimeDisplay(coupon.timeRangeTo)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">All hours</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {!coupon.isFullyRedeemed && !coupon.isExpired && (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center justify-center ${
                              coupon.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {coupon.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                        {coupon.isFullyRedeemed && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 text-center justify-center">
                            Fully Redeemed
                          </span>
                        )}
                        {coupon.isExpired && !coupon.isFullyRedeemed && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 text-center justify-center">
                            Expired
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteCoupon(coupon.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        title="Delete coupon"
                      >
                        <MdDelete className="w-4 h-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Coupon Modal */}
      {isCreateModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New Coupon</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newCoupon.name || ''}
                  onChange={e =>
                    setNewCoupon({ ...newCoupon, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Coupons *
                </label>
                <input
                  type="number"
                  min="1"
                  value={newCoupon.numberOfCoupons || 1}
                  onChange={e =>
                    setNewCoupon({
                      ...newCoupon,
                      numberOfCoupons: Number(e.target.value) || 1,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Applicable Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(day => {
                    const isSelected = selectedDays.has(day.code);
                    return (
                      <button
                        key={day.code}
                        type="button"
                        onClick={() => toggleDay(day.code)}
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        title={day.label}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedDays.size === 7
                    ? ''
                    : `Selected: ${daysOfWeek
                        .filter(day => selectedDays.has(day.code))
                        .map(day => day.label)
                        .join(', ')}`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Type *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="discountType"
                      value="Percentage"
                      checked={newCoupon.discountType === 'Percentage'}
                      onChange={e =>
                        setNewCoupon({
                          ...newCoupon,
                          discountType: e.target.value,
                        })
                      }
                      className="mr-2"
                    />
                    Percentage
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="discountType"
                      value="Fixed"
                      checked={newCoupon.discountType === 'Fixed'}
                      onChange={e =>
                        setNewCoupon({
                          ...newCoupon,
                          discountType: e.target.value,
                        })
                      }
                      className="mr-2"
                    />
                    Fixed
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Value *
                </label>
                <input
                  type="number"
                  value={newCoupon.discountValue || 0}
                  onChange={e =>
                    setNewCoupon({
                      ...newCoupon,
                      discountValue: Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Uses
                </label>
                <input
                  type="number"
                  value={newCoupon.maxUses || 0}
                  onChange={e =>
                    setNewCoupon({
                      ...newCoupon,
                      maxUses: Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Uses Per User
                </label>
                <input
                  type="number"
                  value={newCoupon.maxUsesPerUser || 0}
                  onChange={e =>
                    setNewCoupon({
                      ...newCoupon,
                      maxUsesPerUser: Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid From
                </label>
                <input
                  type="datetime-local"
                  value={newCoupon.validFrom || ''}
                  onChange={e =>
                    setNewCoupon({ ...newCoupon, validFrom: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid To
                </label>
                <input
                  type="datetime-local"
                  value={newCoupon.validTo || ''}
                  onChange={e =>
                    setNewCoupon({ ...newCoupon, validTo: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time Range Validity (Optional)
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">
                      From (24hr format)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="2359"
                      step="1"
                      value={newCoupon.timeRangeFrom || ''}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '') {
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeFrom: null,
                          });
                          return;
                        }
                        const numValue = parseInt(value, 10);
                        if (isNaN(numValue)) return;

                        // Allow values up to 2359
                        if (numValue < 0 || numValue > 2359) return;

                        // Validate 24-hour format: extract hours and minutes
                        const hours = Math.floor(numValue / 100);
                        const minutes = numValue % 100;

                        // Allow partial input (1-3 digits) while typing for better UX
                        // For 4-digit values, validate hours (0-23) and minutes (0-59)
                        if (value.length <= 3) {
                          // Allow partial input while typing
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeFrom: numValue,
                          });
                        } else if (
                          hours >= 0 &&
                          hours <= 23 &&
                          minutes >= 0 &&
                          minutes <= 59
                        ) {
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeFrom: numValue,
                          });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        newCoupon.timeRangeFrom !== null &&
                        newCoupon.timeRangeFrom !== undefined &&
                        newCoupon.timeRangeTo !== null &&
                        newCoupon.timeRangeTo !== undefined &&
                        newCoupon.timeRangeTo.toString().length === 4 &&
                        newCoupon.timeRangeFrom.toString().length === 4 &&
                        newCoupon.timeRangeFrom > newCoupon.timeRangeTo
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="1025 (for 10:25)"
                    />
                  </div>
                  <span className="text-gray-500 mt-6">–</span>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">
                      To (24hr format)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="2359"
                      step="1"
                      value={newCoupon.timeRangeTo || ''}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '') {
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeTo: null,
                          });
                          return;
                        }
                        const numValue = parseInt(value, 10);
                        if (isNaN(numValue)) return;

                        // Allow values up to 2359
                        if (numValue < 0 || numValue > 2359) return;

                        // Validate 24-hour format: extract hours and minutes
                        const hours = Math.floor(numValue / 100);
                        const minutes = numValue % 100;

                        // Allow partial input (1-3 digits) while typing for better UX
                        // For 4-digit values, validate hours (0-23) and minutes (0-59)
                        if (value.length <= 3) {
                          // Allow partial input while typing
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeTo: numValue,
                          });
                        } else if (
                          hours >= 0 &&
                          hours <= 23 &&
                          minutes >= 0 &&
                          minutes <= 59
                        ) {
                          setNewCoupon({
                            ...newCoupon,
                            timeRangeTo: numValue,
                          });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                        newCoupon.timeRangeFrom !== null &&
                        newCoupon.timeRangeFrom !== undefined &&
                        newCoupon.timeRangeTo !== null &&
                        newCoupon.timeRangeTo !== undefined &&
                        newCoupon.timeRangeTo.toString().length === 4 &&
                        newCoupon.timeRangeFrom.toString().length === 4 &&
                        newCoupon.timeRangeFrom > newCoupon.timeRangeTo
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="1330 (for 13:30)"
                    />
                  </div>
                </div>
                {newCoupon.timeRangeFrom !== null &&
                newCoupon.timeRangeFrom !== undefined &&
                newCoupon.timeRangeTo !== null &&
                newCoupon.timeRangeTo !== undefined &&
                newCoupon.timeRangeTo.toString().length === 4 &&
                newCoupon.timeRangeFrom.toString().length === 4 &&
                newCoupon.timeRangeFrom > newCoupon.timeRangeTo ? (
                  <p className="text-xs text-red-600 mt-1">
                    Error: Start time must be before or equal to end time
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    If left blank, the code is valid for all hours. Use 24-hour
                    format as numbers (e.g., 1000 for 10:00, 1330 for 13:30)
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setIsCreateModalVisible(false);
                  resetDays();
                }}
                disabled={isCreating}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCoupon}
                disabled={isCreating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCreating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {isCreating ? 'Creating...' : 'Create Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  );
};

export default CouponManagement;
